/**
 * 深/浅主题（备忘录设计稿 SPEC-tokens 的 theme 枚举：dark / light）。
 *
 * 落在 `<html data-theme>` 上，themes.css 里两套 CSS 变量按它切换——
 * 组件不监听、不重渲，变量换值工具类跟着换。默认深色（设计稿 default: dark）。
 *
 * 持久化走 localStorage：换主题是一次明确的选择，下次打开记住它。
 * 首次没有存储值时不猜系统偏好——设计稿的默认是 dark，就给 dark。
 */

export type Theme = 'dark' | 'light'

const STORAGE_KEY = 'npc-theme'

export function currentTheme(): Theme {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY)
    if (saved === 'dark' || saved === 'light') return saved
  } catch {
    // 隐私模式读不到就给默认
  }
  return 'dark'
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  // index.html 的内联脚本给 html 设过首屏兜底底色（CSS 到达前的窗口）。
  // 到这里 CSS 已就位、body 底色走 themes.css 的变量，把内联值交还回去，
  // 之后换主题底色就只有 CSS 变量这一个权威来源。
  root.style.background = ''
  if (theme === 'light') root.setAttribute('data-theme', 'light')
  else root.removeAttribute('data-theme')
  // lulu-* 色板挂在 data-npc-theme 上，不一起写就会切一半：
  // 顶栏/备忘录换了、chat/项目页还停在旧档（2026-08-29 主题审计）。
  root.setAttribute('data-npc-theme', theme)
  root.style.colorScheme = theme
  try {
    window.localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // 存不上就只改本次会话
  }
}

/** 启动时恢复上次的选择。在 Shell 挂载时调一次。 */
export function restoreTheme(): void {
  applyTheme(currentTheme())
}

export function toggleTheme(): Theme {
  const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark'
  applyTheme(next)
  return next
}
